import { PromiseExtended } from 'dexie';
import { makeClearPublisher } from 'serviceWorker/broadcasts/publishers';
import { documentationFilteringCacheManager } from 'serviceWorker/repository/documentation/documentationFilteringCacheManager/index';
import { documentationContract } from 'shared/domain/documentation/documentationContract';
import {
  DocumentationEntity,
  DocumentationInsertEntity,
  DocumentationLocalInsertEntity,
  VersionEntity,
} from 'serviceWorker/repository/documentation/entity';
import { documentationInDtoToCreateEntity } from 'serviceWorker/repository/documentation/mappings';
import { DocumentationInDto } from 'shared/dtos/in/documentation';
import { Changes } from 'shared/types/commonEntities';
import { LogLevel } from 'shared/types/logger';
import { nowISO } from 'shared/utils/date/dates';
import { normalizeForSearch } from 'shared/utils/search';
import {
  makeDefaultCount,
  makeDefaultGetMany,
  makeDefaultGetManyByIds,
  makeDefaultGetOne,
  makeDefaultRemoveBatch,
  makeGetManyUndeleted,
} from '../defaultDaoFactories';
import { entityDbClearFactory } from '../entityDbClearFactory';
import {
  getErrorMsg,
  isDexieConstraintError,
  logWithRethrow,
  Operation,
} from '../helpers';
import db from '../index';
import { wrapQuery } from '../queryWrapper';
import { clear as documentationsServiceClear } from './documentationsService';
import { assignRemoteIdFactory } from '../assignRemoteIdFactory';
import { hasChangesToSend, isUnsentVersion, mergeStoredVersions } from './documentationVersions';

const publishClearDocumentations = makeClearPublisher(documentationContract);

/** Cleared documentations drop their cached filtering before the page is told. */
function publishDocumentationsCleared(): void {
  documentationFilteringCacheManager.invalidateAllDocumentationFilteringCaches();
  publishClearDocumentations();
}

/**
 * Drops the stored documentations but the ones with changes still to send:
 * a queued upload, edit or deletion finds its documentation only in that row
 * (a stale sync key, 499, clears the table on every device at once).
 */
export const clear = entityDbClearFactory(
  db,
  ['documentations', 'documentationsService'],
  async () => {
    await db.documentations.filter((documentation) => !hasChangesToSend(documentation)).delete();
  },
  documentationsServiceClear,
  publishDocumentationsCleared
);

const update = wrapQuery(
  db,
  (
    documentationFindStrategy: CallableFunction,
    data: DocumentationInsertEntity
  ): PromiseExtended<any> => {
    return documentationFindStrategy().then(
      (documentation: DocumentationEntity) => {
        if (!documentation) {
          throw new Error('Cannot find document to update');
        } else {
          const documentationWithLocalData =
            mergeStoredVersions(data, documentation);

          return db.documentations.update(
            documentation.localId,
            documentationWithLocalData
          );
        }
      }
    );
  }
);

export const quantity = makeDefaultCount<DocumentationEntity>(
  db,
  db.documentations
);

export const get = makeDefaultGetMany<DocumentationEntity>(
  db,
  db.documentations
);

export const getUndeleted = makeDefaultGetMany<DocumentationEntity>(
  db,
  db.documentations,
  { deleted: 0 }
);

export const getByQuery = makeDefaultGetMany<DocumentationEntity>(
  db,
  db.documentations
);

export const getUndeletedByParentId = makeGetManyUndeleted<
  string,
  DocumentationEntity
>(db, db.documentations, 'parentId');

export const getOneByRemote = makeDefaultGetOne<
  string,
  DocumentationEntity
>(db, db.documentations, '_id');

export const getOne = makeDefaultGetOne<number, DocumentationEntity>(
  db,
  db.documentations,
  'localId'
);
export const getByIds = makeDefaultGetManyByIds<
  number,
  DocumentationEntity
>(db, db.documentations, 'localId');

export const getSearched = wrapQuery(
  db,
  async (searchPhrase: string): Promise<DocumentationEntity[]> => {
    return db.documentations
      .toArray()
      .then((docs) => {
        return docs.filter((doc: DocumentationEntity) => {
          return (
            normalizeForSearch(doc.name).includes(
              normalizeForSearch(searchPhrase)
            ) ||
            normalizeForSearch(doc.number || '').includes(
              normalizeForSearch(searchPhrase)
            )
          );
        });
      })
      .catch((e) =>
        logWithRethrow({
          logLevel: LogLevel.INFO,
          msg: getErrorMsg(Operation.filter, 'documentations'),
          errorObj: e,
          additionalInfo: { query: { searchPhrase } },
        })
      );
  }
);

const upsertBatch = wrapQuery(
  db,
  async (data: DocumentationInDto[]): Promise<number> => {
    if (data.length === 0) return Promise.resolve(0);

    const entityMap = new Map<string, DocumentationInsertEntity>();
    for (const doc of data) {
      const entity = documentationInDtoToCreateEntity(doc);
      if (entity._id) {
        entityMap.set(entity._id, entity);
      }
    }
    const uniqueEntities = Array.from(entityMap.values());
    const incomingIds = Array.from(entityMap.keys());

    if (incomingIds.length === 0) return Promise.resolve(0);

    const existingRecords = await db.documentations
      .where('_id')
      .anyOf(incomingIds)
      .toArray();

    const existingMap = new Map<string, DocumentationEntity>();
    for (const record of existingRecords) {
      if (record._id) {
        existingMap.set(record._id, record);
      }
    }

    const entitiesToUpsert = uniqueEntities.map((entity) => {
      const existing = existingMap.get(entity._id!);
      if (existing) {
        const preserved = mergeStoredVersions(entity, existing);
        return { ...preserved, localId: existing.localId };
      }
      return entity;
    });

    return (
      db.documentations
        // @ts-ignore localId may or may not be present
        .bulkPut(entitiesToUpsert)
        .catch((e) => {
          logWithRethrow({
            logLevel: LogLevel.INFO,
            msg: getErrorMsg(Operation.upsertBatch, 'documentations'),
            errorObj: e,
            additionalInfo: { query: { data } },
          });
        })
    );
  }
);

export const fastAddBatch = wrapQuery(
  db,
  async (data: DocumentationInDto[]): Promise<number> => {
    if (data.length === 0) return Promise.resolve(0);

    const entityMap = new Map<string, DocumentationInsertEntity>();
    for (const doc of data) {
      const entity = documentationInDtoToCreateEntity(doc);
      if (entity._id) {
        entityMap.set(entity._id, entity);
      }
    }
    const incomingIds = Array.from(entityMap.keys());

    if (incomingIds.length === 0) return Promise.resolve(0);

    const existingRecords = await db.documentations
      .where('_id')
      .anyOf(incomingIds)
      .toArray();

    const existingIdSet = new Set(existingRecords.map((r) => r._id));
    const newEntities = Array.from(entityMap.values()).filter(
      (e) => !existingIdSet.has(e._id!)
    );

    if (newEntities.length === 0) return Promise.resolve(0);

    try {
      return await db.documentations
        // @ts-ignore DocumentationEntityToCreate localId is an autoincrement
        .bulkPut(newEntities);
    } catch (e: unknown) {
      if (isDexieConstraintError(e)) {
        return upsertBatch(data);
      }
      logWithRethrow({
        logLevel: LogLevel.INFO,
        msg: getErrorMsg(Operation.upsertBatch, 'documentations'),
        errorObj: e,
        additionalInfo: { query: { data } },
      });
      return 0;
    }
  }
);

export const addBatch = upsertBatch;
export const updateBatch = upsertBatch;

export const addLocal = wrapQuery(
  db,
  (data: DocumentationLocalInsertEntity): PromiseExtended<number> => {
    // @ts-ignore DocumentationEntityToCreate localId is an autoincrement
    return db.documentations.add(data).catch((e) =>
      logWithRethrow({
        logLevel: LogLevel.INFO,
        msg: getErrorMsg(Operation.add, 'documentations'),
        errorObj: e,
        additionalInfo: { query: { data } },
      })
    );
  }
);

export const removeBatch = makeDefaultRemoveBatch<
  string,
  DocumentationEntity
>(db, db.documentations, '_id');

export const remove = wrapQuery(
  db,
  async (id: number): Promise<unknown> => {
    return db.documentations.delete(id).catch((e) =>
      logWithRethrow({
        logLevel: LogLevel.INFO,
        msg: getErrorMsg(Operation.remove, 'documentations'),
        errorObj: e,
        additionalInfo: { query: { id } },
      })
    );
  }
);

export const updateOne = wrapQuery(
  db,
  async (
    localId: number,
    changes: Changes<DocumentationEntity>,
    options?: { skipModifyTime: boolean }
  ): Promise<any> => {
    const modifiedAt = options?.skipModifyTime
      ? {}
      : { modifiedAt: nowISO() };
    return db.documentations
      .update(localId, { ...changes, ...modifiedAt })
      .catch((e) =>
        logWithRethrow({
          logLevel: LogLevel.INFO,
          msg: getErrorMsg(Operation.updateOne, 'documentations'),
          errorObj: e,
          additionalInfo: { query: changes },
        })
      );
  }
);

export const updateOneVersion = wrapQuery(
  db,
  async (
    localId: number,
    versionId: string,
    changes: Partial<Omit<VersionEntity, '_id'>>
  ): Promise<number> => {
    const documentation = await db.documentations.get(localId);
    if (!documentation) {
      throw new Error('Documentation not found');
    }

    const versionIndex = documentation.versions.findIndex(
      (version) => version._id === versionId
    );
    if (versionIndex === -1) {
      throw new Error('Version not found');
    }

    const updatedVersions = [...documentation.versions];
    updatedVersions[versionIndex] = {
      ...updatedVersions[versionIndex],
      ...changes,
      modifiedAt: nowISO(),
    };

    return db.documentations
      .update(localId, {
        versions: updatedVersions,
        modifiedAt: nowISO(),
      })
      .catch((e) =>
        logWithRethrow({
          logLevel: LogLevel.INFO,
          msg: getErrorMsg(Operation.updateOne, 'documentations'),
          errorObj: e,
          additionalInfo: { query: { localId, versionId, changes } },
        })
      );
  }
);

/** The backend's id for the version being uploaded from this device, given as soon as the backend creates it. */
export const assignUnsentVersionId = wrapQuery(
  db,
  async (localId: number, versionId: string): Promise<number> => {
    return db.documentations
      .where('localId')
      .equals(localId)
      .modify((documentation) => {
        const unsent = documentation.versions.find((version) => !version._id && isUnsentVersion(version));
        if (unsent) {
          // @ts-ignore _id is readonly on the entity, set once here
          unsent._id = versionId;
        }
      })
      .catch((e) =>
        logWithRethrow({
          logLevel: LogLevel.INFO,
          msg: getErrorMsg(Operation.updateOne, 'documentations'),
          errorObj: e,
          additionalInfo: { query: { localId, versionId } },
        })
      );
  }
);

/** The server id of a documentation created here; drops a copy a sync stored first (PT-7278). */
export const assignRemoteId = assignRemoteIdFactory(db, 'documentations', db.documentations);
